/**
 * Joisst Platform Connector SDK
 * Simplifies session synchronization, themes, and credit spending.
 */
const Joisst = {
  // Real-time reactive user state
  user: {
    userId: "anonymous", // Canonical user identifier
    id: "anonymous",     // Legacy alias for backwards compatibility
    hearts: 0,
    brains: 0,
    language: "en",
    theme: "light",
    isAnonymous: true,
    referralCode: "",
    activeAffiliateRef: ""
  },

  // Base URL of the Joisst Platform
  platformUrl: "https://api.joisst.com",

  // Environment Check
  isSandbox() {
    const host = window.location.hostname;
    return !(host === "joisst.com" || host.endsWith(".joisst.com"));
  },

  // Centralized theme applicator
  applyTheme(theme) {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },

  // Event dispatchers
  dispatchSessionChange(user) {
    window.dispatchEvent(new CustomEvent('joisst-session-changed', { detail: user }));
  },

  dispatchUserChange(user) {
    window.dispatchEvent(new CustomEvent('joisst-user-changed', { detail: user }));
  },

  // Centralized user update logic
  updateUser(newData, emitUserChanged = true) {
    const prevId = Joisst.user.userId ?? Joisst.user.id;
    const incomingUserId = newData.userId ?? newData.id;
    
    Joisst.user = {
      ...Joisst.user,
      ...newData,
      userId: incomingUserId ?? Joisst.user.userId ?? "anonymous",
      id: incomingUserId ?? Joisst.user.id ?? "anonymous", // Legacy alias for backwards compatibility
      hearts: typeof newData.hearts === 'number' ? newData.hearts : (typeof Joisst.user.hearts === 'number' ? Joisst.user.hearts : 0),
      brains: typeof newData.brains === 'number' ? newData.brains : (typeof Joisst.user.brains === 'number' ? Joisst.user.brains : 0),
      language: newData.language ?? Joisst.user.language ?? "en",
      theme: newData.theme ?? Joisst.user.theme ?? "light",
      isAnonymous: typeof newData.isAnonymous === 'boolean'
        ? newData.isAnonymous
        : (incomingUserId ? incomingUserId.startsWith('anon_') : (typeof Joisst.user.isAnonymous === 'boolean' ? Joisst.user.isAnonymous : true)),
      referralCode: newData.referralCode ?? Joisst.user.referralCode ?? "",
      activeAffiliateRef: newData.activeAffiliateRef ?? Joisst.user.activeAffiliateRef ?? ""
    };

    // Synchronize dark/light theme classes on root HTML element
    Joisst.applyTheme(Joisst.user.theme);

    // Emit reactive user update events if requested
    if (emitUserChanged) {
      Joisst.dispatchUserChange(Joisst.user);
    }

    // Emit session change event only when the user ID changes
    const currentId = Joisst.user.userId;
    if (prevId !== currentId) {
      Joisst.dispatchSessionChange(Joisst.user);
    }
  },

  // Robust Fetch for Sandbox Mode
  async fetchJson(url, options = {}) {
    const response = await fetch(url, options);
    const text = await response.text();
    
    if (!response.ok) {
      let errorMsg = `Server Error (${response.status})`;
      try {
        const errData = JSON.parse(text);
        if (errData && errData.message) {
          errorMsg = errData.message;
        }
      } catch {
        if (text) errorMsg = text.substring(0, 100);
      }
      throw new Error(errorMsg);
    }

    try {
      return JSON.parse(text);
    } catch (err) {
      const snippet = text ? text.substring(0, 100) : "(empty)";
      console.error(`[Joisst SDK] JSON.parse failed for ${url}. Raw content: ${snippet}`, err);
      throw new Error(`Invalid JSON response from sandbox API. Raw content: ${snippet}`);
    }
  },

  // Spend credit actions
  spendHearts: async (amount = 1) => {
    if (!Joisst.isSandbox()) {
      if (!window.JoisstNavbar) throw new Error("Joisst SDK is not loaded yet.");
      return window.JoisstNavbar.spendHearts(amount);
    } else {
      const data = await Joisst.fetchJson('/api/hearts?action=spend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      Joisst.updateUser({ hearts: data.hearts });
      return data;
    }
  },

  spendBrains: async (amount = 1) => {
    if (!Joisst.isSandbox()) {
      if (!window.JoisstNavbar) throw new Error("Joisst SDK is not loaded yet.");
      return window.JoisstNavbar.spendBrains(amount);
    } else {
      const data = await Joisst.fetchJson('/api/brains?action=spend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount })
      });
      Joisst.updateUser({ brains: data.brains });
      return data;
    }
  },

  // Directs the user to register or sign in to protect their data
  redirectToRegister: () => {
    const currentUrl = encodeURIComponent(window.location.href);
    window.location.href = `${Joisst.platformUrl}/auth?return_to=${currentUrl}`;
  },

  // Safe Guard before performing any persistence operations
  assertUserAuthenticated: () => {
    if (Joisst.user.isAnonymous) {
      alert("Please sign up or log in to save your progress and keep your data secure!");
      Joisst.redirectToRegister();
      return false;
    }
    return true;
  },

  // Local user synchronization
  async syncLocalUser() {
    try {
      const data = await Joisst.fetchJson('/api/user');
      if (data && Joisst.isSandbox()) {
        Joisst.updateUser(data);
      }
    } catch (e) {
      console.warn("[Joisst SDK] Failed to sync local user:", e.message);
    }
  },

  // Initialize and listen to Joisst Platform updates
  init() {
    const syncUser = () => {
      if (window.JoisstNavbar && window.JoisstNavbar.user) {
        Joisst.updateUser(window.JoisstNavbar.user, false);
      }
    };

    // Synchronize instantly when Navbar loads or state changes
    window.addEventListener('joisst-ready', syncUser);
    window.addEventListener('joisst-user-changed', syncUser);
    
    // Listen for live theme modifications
    window.addEventListener('joisst-theme-changed', (e) => {
      Joisst.user.theme = e.detail;
      Joisst.applyTheme(e.detail);
    });

    // Listen for language alterations
    window.addEventListener('joisst-language-changed', (e) => {
      Joisst.user.language = e.detail;
      window.dispatchEvent(new CustomEvent('joisst-lang-changed', { detail: e.detail }));
    });

    // Handle immediate load if script completed early
    if (window.JoisstNavbar) {
      syncUser();
    } else if (Joisst.isSandbox()) {
      Joisst.syncLocalUser();
    }
  }
};

// Expose the SDK globally
window.Joisst = Joisst;

// Start the SDK connector
Joisst.init();